import { alpha, createTheme } from '@mui/material/styles';
import { BRAND_FONT_FAMILY, BRAND_RADIUS } from './brandTokens';

const HEADING_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

/**
 * MUI theme built from a derived brand palette (see buildBrandPalette).
 * The concrete palette is also available as `theme.brand` for code that needs
 * real colour values (charts, canvas, colour maths).
 */
const createAppTheme = (palette) =>
  createTheme({
    brand: palette,
    palette: {
      primary: {
        main: palette.primary,
        dark: palette.primaryDark,
        light: palette.primaryLight,
        contrastText: palette.onPrimary,
      },
      secondary: {
        main: palette.accent,
        dark: palette.accentDark,
        light: palette.accentLight,
        contrastText: palette.onAccent,
      },
      background: {
        default: palette.background,
        paper: palette.surface,
      },
      text: {
        primary: palette.text,
        secondary: alpha(palette.text, 0.72),
      },
      divider: alpha(palette.text, 0.14),
    },
    typography: {
      fontFamily: BRAND_FONT_FAMILY,
      ...Object.fromEntries(HEADING_VARIANTS.map((variant) => [variant, { fontWeight: 700, letterSpacing: '-0.02em' }])),
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: BRAND_RADIUS.button,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          color: 'secondary',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiTabs: {
        defaultProps: {
          indicatorColor: 'secondary',
          textColor: 'secondary',
        },
      },
      MuiSwitch: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiRadio: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiCircularProgress: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiLinearProgress: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: palette.accentText,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: BRAND_RADIUS.card,
            border: `1px solid ${alpha(palette.text, 0.14)}`,
            boxShadow: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: BRAND_RADIUS.card,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
    },
  });

export default createAppTheme;
